// Default "most important PII first" ordering for entity-type displays (e.g. the
// Sensitive Data Landscape "Entity types found" tiles). Highest-risk direct
// identifiers lead, followed by health identifiers, then names, contact, location,
// and finally low-sensitivity/technical fields.
//
// This is the shipped default. Operators can override the order install-wide via
// Advanced Config → Entity Levels (persisted as `entityOrder` on the advanced-config
// blob); consumers should call sortEntityCodesByPriority(codes, override) with that
// saved list, falling back to this default when it is empty/absent.
const DEFAULT_ENTITY_PRIORITY = [
  // Government / financial direct identifiers — highest sensitivity
  'SSN',
  'CANADIAN_SIN',
  'CREDIT_CARD',
  'ACCOUNT_NUMBER',
  'PASSPORT',
  'DRIVING_LC',
  // Credentials & secrets (2026-08-18) — direct-compromise class: an exposed
  // credential is immediately actionable by an attacker, so the family sits
  // with the direct identifiers, ahead of PHI.
  'PASSWORD',
  'PRIVATE_KEY',
  'CLOUD_ACCESS_KEY',
  'VENDOR_API_KEY',
  'PAYMENT_API_KEY',
  'JWT_TOKEN',
  'CONNECTION_STRING',
  'AUTH_SECRET',
  'SEED_PHRASE',
  'KEY_MATERIAL_FILE',
  // Health-plan / medical identifiers (PHI)
  'MEDICARE_BENEFICIARY_IDENTIFIER_MBI',
  'MEDICARE_BENEFICIARY_IDENTIFIER_HICN',
  'MEDICARE_BENEFICIARY_IDENTIFIER',
  'MEDI_CAL_CIN',
  'CA_HEALTH_NUMBER',
  'MEMBER_ID',
  'INSURANCE_PLAN_ID',
  'INSURANCE',
  'NPI',
  'PATIENT_ID',
  // Clinical content (PHI)
  'DIAGNOSIS',
  'DRUG',
  'IMMUNIZATION',
  // Dates tied to a person
  'BIRTH_DATE',
  'DEATH_DATE',
  'PATIENT_DATE',
  // Names / person
  'FIRST_NAME',
  'LAST_NAME',
  'PERSON',
  'SUFFIX',
  // Contact
  'EMAIL',
  'TELEPHONE_NO',
  // Address / location
  'ADDRESS_STREET',
  'ADDRESS_LINE2',
  'PO_BOX',
  'CITY',
  'COUNTY',
  'STATE',
  'ZIP',
  'COUNTRY',
  'LOCATION',
  'LATITUDE',
  'LONGITUDE',
  // Technical / low-sensitivity
  'IP_ADDRESS',
  'WEB',
  'ORG',
  'UUID',
];

// Rank lookup for a single code. Codes present in `order` rank by their index;
// anything not listed sorts after all ranked codes (stable, alphabetical among
// themselves is left to the caller's tiebreak).
const priorityRank = (code, order) => {
  const idx = order.indexOf(code);
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

// Sort an array of entity-type code strings by priority. `override` (if a non-empty
// array) wins; otherwise the shipped default is used. Unknown codes keep their
// original relative order (stable) after the ranked ones.
const sortEntityCodesByPriority = (codes, override) => {
  const order = Array.isArray(override) && override.length > 0 ? override : DEFAULT_ENTITY_PRIORITY;
  return [...codes].sort((a, b) => priorityRank(a, order) - priorityRank(b, order));
};

// Sort an array of objects that each carry an entity-type code, given an accessor.
// Same rule as sortEntityCodesByPriority; stable for equal ranks.
const sortByEntityPriority = (items, getCode, override) => {
  const order = Array.isArray(override) && override.length > 0 ? override : DEFAULT_ENTITY_PRIORITY;
  return [...items].sort((a, b) => priorityRank(getCode(a), order) - priorityRank(getCode(b), order));
};

export {
  DEFAULT_ENTITY_PRIORITY,
  priorityRank,
  sortEntityCodesByPriority,
  sortByEntityPriority,
};
